import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import api from '@/api/client';
import type { ApiResponse } from '@/types/api';
import type { HrPreboardingItem } from '@/types/hr';

type PortalOffer = {
  id: number;
  offeredSalary?: number;
  currencyCode?: string;
  joiningDate?: string;
  statusCode: string;
  offerContent?: string;
};

type PortalData = {
  candidateName: string;
  stage: string;
  sessionStatus: string;
  candidateId?: number;
  items: HrPreboardingItem[];
  offers?: PortalOffer[];
};

export function HrCandidatePortalPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<number | null>(null);
  const [offerActing, setOfferActing] = useState<number | null>(null);
  const fileRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.get<ApiResponse<PortalData>>(`/v1/public/candidate-portal/${token}`);
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const completeItem = async (itemId: number, fileUrl?: string) => {
    if (!token) return;
    setActingId(itemId);
    try {
      if (fileUrl) {
        await api.post(`/v1/public/candidate-portal/${token}/items/${itemId}/complete`, null, {
          params: { fileUrl },
        });
      } else {
        await api.post(`/v1/public/candidate-portal/${token}/items/${itemId}/accept`);
      }
      await load();
    } finally {
      setActingId(null);
    }
  };

  const uploadItem = async (itemId: number, file: File) => {
    if (!token) return;
    setActingId(itemId);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/v1/public/candidate-portal/${token}/items/${itemId}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } finally {
      setActingId(null);
    }
  };

  const acceptOffer = async (offerId: number) => {
    setOfferActing(offerId);
    try {
      await api.post(`/v1/public/candidate-portal/${token}/offers/${offerId}/accept`);
      notifySuccess('Offer accepted — preboarding will begin');
      await load();
    } catch {
      notifyError('Could not accept offer — it may still need HR approval');
    } finally {
      setOfferActing(null);
    }
  };

  const declineOffer = async (offerId: number) => {
    setOfferActing(offerId);
    try {
      await api.post(`/v1/public/candidate-portal/${token}/offers/${offerId}/decline`);
      await load();
    } finally {
      setOfferActing(null);
    }
  };

  if (loading) return <div className="mx-auto max-w-lg p-8 text-center text-slate-500">Loading…</div>;
  if (!data) return <div className="mx-auto max-w-lg p-8 text-center text-red-600">Invalid or expired portal link.</div>;

  const done = data.items.filter((i) => i.statusCode === 'COMPLETED').length;
  const pendingOffer = data.offers?.find((o) => o.statusCode === 'SENT');

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 dark:bg-slate-900">
      <div className="mx-auto max-w-lg space-y-6">
        {pendingOffer && (
          <div className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm dark:border-primary-800 dark:bg-slate-800">
            <h2 className="text-lg font-semibold">Offer letter</h2>
            <p className="mt-1 text-sm text-slate-500">
              Salary: {pendingOffer.offeredSalary ?? '—'} {pendingOffer.currencyCode ?? ''}
              {pendingOffer.joiningDate ? ` · Joining ${pendingOffer.joiningDate}` : ''}
            </p>
            {pendingOffer.offerContent && (
              <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{pendingOffer.offerContent}</p>
            )}
            <div className="mt-4 flex gap-2">
              <Button loading={offerActing === pendingOffer.id} onClick={() => void acceptOffer(pendingOffer.id)}>
                Accept offer
              </Button>
              <Button variant="secondary" loading={offerActing === pendingOffer.id} onClick={() => void declineOffer(pendingOffer.id)}>
                Decline
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <h1 className="text-xl font-semibold">Welcome, {data.candidateName}</h1>
          <p className="mt-1 text-sm text-slate-500">
            Preboarding checklist · {done}/{data.items.length} complete
          </p>
          <ul className="mt-6 space-y-3">
            {data.items.map((item) => (
              <li key={item.id} className="rounded border p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span>{item.itemLabel}</span>
                  {item.statusCode === 'COMPLETED' ? (
                    <span className="text-green-600">Done</span>
                  ) : item.itemCategory === 'DOCUMENT' ? (
                    <div className="flex items-center gap-2">
                      <input
                        ref={(el) => {
                          fileRefs.current[item.id] = el;
                        }}
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void uploadItem(item.id, file);
                        }}
                      />
                      <Button
                        size="sm"
                        loading={actingId === item.id}
                        onClick={() => fileRefs.current[item.id]?.click()}
                      >
                        Upload
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" loading={actingId === item.id} onClick={() => void completeItem(item.id)}>
                      Submit
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {done === data.items.length && (
            <p className="mt-4 text-center text-sm text-green-600">
              All items submitted. HR will continue your onboarding.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function notifySuccess(msg: string) {
  import('@/core/toast/notify').then((m) => m.notify.success(msg));
}

function notifyError(msg: string) {
  import('@/core/toast/notify').then((m) => m.notify.error(msg));
}
